import { useEffect, useState } from "react";
// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import add from "assets/images/add.png";
import { useNavigate } from "react-router-dom";
import { useGetAllProductsMutation } from "services/product";
import ShowToast from "components/MDToast";
import MDAvatar from "components/MDAvatar";
import WarnModal from "components/DeleteModal";
import { Box, IconButton } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import DeleteIcon from "@mui/icons-material/Delete";
import Pagination from "components/Pagination";
import { useDeleteProductMutation } from "services/product";
import Loader from "helpers/constants/Loader";
import { useLazyGetCmsQuery } from "services/settings";
import { isValidInput } from "utils/validations";
import SearchBar from "components/SearchBar";
import { STORAGE_KEYS } from "helpers";
import { getFromStorage } from "helpers";
import useTranslation from "hooks/Translation";

const ManageProducts = () => {
  const t = useTranslation();
  const navigate = useNavigate();
  const currency = getFromStorage(STORAGE_KEYS.currency);
  const [open, setOpen] = useState(false);
  const [deleteProduct] = useDeleteProductMutation();
  const [getCms] = useLazyGetCmsQuery();
  const [getProducts] = useGetAllProductsMutation();
  const [productData, setProductData] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(10);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [cmsData, setCmsData] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const getAllCms = async () => {
    try {
      setLoading(true);
      const response = await getCms({}).unwrap();
      if (response?.status === 200) {
        setCmsData(response?.data);

        setLoading(false);
      } else {
        setCmsData(response?.data);
      }
    } catch (error) {
      setLoading(false);
      // ShowToast(error?.data?.message || "", "error");
    }
  };

  const getAllProducts = async (page) => {
    let body = {
      start: page,
      length: limit,
      search: debouncedSearchTerm,
    };
    setLoading(true);
    try {
      const response = await getProducts(body).unwrap();
      if (response?.status === 200) {
        setLoading(false);
        setProductData(response?.data?.response_object);
        setTotalRecords(response?.data?.total_records);
      } else {
        setLoading(false);
        setProductData([]);
      }
    } catch (error) {
      setLoading(false);
      ShowToast(error?.data?.message || "", "error");
    }
  };

  const handleDeleteProduct = async (id) => {
    try {
      const response = await deleteProduct(id).unwrap();
      if (response?.status === 200) {
        ShowToast("Product deleted successfully", "success");
        getAllProducts();
        setOpen(false);
      }
    } catch (error) {
      console.log(error, "errror");
      ShowToast(error?.data?.message || "", "error");
    }
  };

  const totalPages = Math.ceil(totalRecords / limit);

  const Author = ({ image, name, email }) => (
    <MDBox
      display="flex"
      alignItems="center"
      lineHeight={1}
      sx={{ minWidth: "160px" }}
    >
      <MDAvatar src={image} name={name} size="sm" />
      <MDBox ml={2} lineHeight={1}>
        <MDTypography display="block" variant="button" fontWeight="medium">
          {name}
        </MDTypography>
        <MDTypography variant="caption">{email}</MDTypography>
      </MDBox>
    </MDBox>
  );

  const Job = ({ title, description }) => (
    <MDBox lineHeight={1} textAlign="left" sx={{ minWidth: "140px" }}>
      <MDTypography display="block" variant="caption" fontWeight="medium">
        {title}
      </MDTypography>
      <MDTypography variant="caption">{description}</MDTypography>
    </MDBox>
  );

  const columns = [
    { Header: t.main.products, accessor: "author", align: "left" },
    { Header: t.main.category, accessor: "price", align: "left" },
    { Header: t.main.pricePerUnit, accessor: "status", align: "left" },
    { Header: t.main.quantityPerUnit, accessor: "employed", align: "left" },
    { Header: t.main.availableStock, accessor: "stock", align: "left" },
    { Header: t.main.actions, accessor: "action", align: "center" },
  ];

  const rows = productData?.map((product) => ({
    author: (
      <Author
        image={product?.product_image[0]?.media_url}
        name={product.product_name_en}
      />
    ),
    price: (
      <Job title={product?.sub_category.category?.category_name_en || "N/A"} />
    ),
    status: (
      <Job
        title={`${currency === "USD" ? "USD" : "AED"} ${
          product?.price_per_unit
        }`}
      />
    ),
    employed: (
      <Job
        title={` ${product?.quantity_per_unit || "N/A"} ${
          product?.product_quantity_type === 1 ? "Kg" : "Litre"
        }`}
      />
    ),
    stock: (
      <Job
        title={`${product?.total_quantity || "N/A"} ${
          product?.product_quantity_type === 1 ? "Kg" : "Litre"
        }`}
      />
    ),
    action: (
      <Box className="table_actions">
        <IconButton
          size="small"
          onClick={() => navigate(`/manage-products/details/${product.id}`)}
        >
          <VisibilityIcon />
        </IconButton>

        <IconButton
          size="small"
          onClick={() => navigate(`/manage-products/${product.id}`)}
        >
          <ModeEditIcon />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => {
            setSelectedId(product.id);
            setOpen(true);
          }}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    ),
  }));

  useEffect(() => {
    getAllProducts(page);
  }, [page, debouncedSearchTerm]);

  useEffect(() => {
    getAllCms();
  }, []);

  return (
    <DashboardLayout>
      <Loader loading={loading} />
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                // variant="gradient"
                bgColor="theme-1"
                borderRadius="lg"
                coloredShadow="info"
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <MDTypography variant="h6" color="white">
                  {t.main.productsTable}
                </MDTypography>

                <div
                  onClick={() => {
                    if (!cmsData) {
                      ShowToast(t.error.addSettings, "error");
                    } else {
                      navigate("/manage-products/add");
                    }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <MDTypography
                    sx={{
                      textDecoration: "underline", // Adds an underline
                      paddingRight: 1,
                    }}
                    variant="h6"
                    color="white"
                  >
                    {t.main.addProducts}
                  </MDTypography>
                  <img src={add} style={{ width: 20, height: 20 }} />
                </div>
              </MDBox>

              <MDBox pt={2} pl={2} pr={2}>
                <div style={{ display: "flex", gap: 10 }}>
                  <SearchBar
                    value={searchTerm}
                    searchTerm={searchTerm}
                    onCross={() => setSearchTerm("")}
                    setDebouncedSearchTerm={setDebouncedSearchTerm}
                    onChange={(val) => {
                      if (isValidInput(val.target.value)) {
                        setSearchTerm(val.target.value);
                      }
                    }}
                  />
                </div>
              </MDBox>

              <MDBox pt={3} pl={2} pr={2}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
              {productData?.length > 0 && (
                <MDBox p={2}>
                  <Pagination
                    setPage={setPage}
                    module={productData}
                    page={page}
                    onPageChange={setPage}
                    totalPages={totalPages}
                    limit={limit}
                    setLimit={setLimit}
                  />
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <WarnModal
        open={open}
        setOpen={setOpen}
        handleDelete={() => handleDeleteProduct(selectedId)}
        name={t.main.products}
      />
    </DashboardLayout>
  );
};

export default ManageProducts;
